import React, { useRef, useState } from "react";
import { FaPrint } from "react-icons/fa";
import { useToast } from "../../../components/Toast";
import { colors, spacing, typography, btnSecondary } from "../../../utils/designTokens";
import { generarPresupuestoPdf } from "../../../utils/presupuestoPdf";

// Hoja A4 a 96 dpi: generarPresupuestoPdf captura cada .page como una página.
const PAGE = { width: 794, height: 1123 };

const fechaLarga = (ms) =>
  new Date(Number(ms)).toLocaleString("es-UY", {
    timeZone: "America/Montevideo", weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
const hora = (ms) =>
  new Date(Number(ms)).toLocaleTimeString("es-UY", { timeZone: "America/Montevideo", hour: "2-digit", minute: "2-digit" });

/** Citaciones que se entregan en papel: las de canal "en mano" (aún sin entregar) y las que fallaron. */
export const requierePapel = (inv) =>
  (inv.canal === "en_mano" && inv.estadoEnvio !== "en_mano_entregada") ||
  inv.estadoEnvio === "en_mano_pendiente" || inv.estadoEnvio === "fallido" || inv.estadoEnvio === "omitido";

function Hoja({ a, inv, edificioNombre }) {
  const nombre = (a.edificio && a.edificio.nombre) || edificioNombre || "el edificio";
  const direccion = (a.edificio && a.edificio.direccion) || "";
  const segunda = Number(a.inicioMs) + Number(a.minutosSegunda || 60) * 60000;
  const lugar =
    a.modalidad === "remota"
      ? `Por videollamada (el enlace se envía a cada propietario). Domicilio del edificio: ${direccion || nombre}.`
      : a.modalidad === "mixta"
        ? `${a.lugar || direccion} o por videollamada.`
        : a.lugar || direccion;
  const unidades = (inv.unidades || []).map((u) => u.etiqueta).join(", ");
  const texto = { fontSize: 13, lineHeight: 1.55, color: "#111827" };
  return (
    <div
      className="page"
      style={{
        width: PAGE.width, height: PAGE.height, boxSizing: "border-box", padding: "64px 72px",
        background: "#ffffff", fontFamily: "'FT Habit', -apple-system, sans-serif", fontWeight: 400,
        display: "flex", flexDirection: "column", gap: 16,
      }}
    >
      <div style={{ fontSize: 12, color: "#4b5563" }}>{nombre}{direccion ? ` · ${direccion}` : ""}</div>
      <div style={{ fontSize: 20, color: "#111827" }}>
        Citación a asamblea {a.tipo === "extraordinaria" ? "extraordinaria" : "ordinaria"} de copropietarios
      </div>
      <div style={texto}>
        Sr./Sra. <strong style={{ fontWeight: 400, textDecoration: "underline" }}>{inv.nombre || "Propietario/a"}</strong>
        {unidades ? `, propietario/a de ${unidades}` : ""}:
      </div>
      <div style={texto}>
        Se le cita a la asamblea de copropietarios de {nombre} del día <strong style={{ fontWeight: 400 }}>{fechaLarga(a.inicioMs)}</strong>,
        en primera convocatoria a las {hora(a.inicioMs)} y en segunda convocatoria a las {hora(segunda)}.
      </div>
      <div style={texto}>Lugar: {lugar}</div>
      <div style={texto}>
        <div style={{ marginBottom: 6 }}>Orden del día:</div>
        <ol style={{ margin: 0, paddingLeft: 22 }}>
          {(a.ordenDelDia || []).map((p) => (
            <li key={p.id} style={{ marginBottom: 4 }}>
              {p.titulo}
              {p.detalle ? <div style={{ color: "#4b5563", fontSize: 12 }}>{p.detalle}</div> : null}
            </li>
          ))}
        </ol>
      </div>
      <div style={{ ...texto, color: "#4b5563", fontSize: 12 }}>
        Si no puede asistir, puede hacerse representar con un poder escrito. La administración.
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ borderTop: "1px dashed #9ca3af", paddingTop: 16, ...texto }}>
        <div style={{ marginBottom: 18 }}>Constancia de recepción — recibí la presente citación.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "28px 32px" }}>
          <div>Firma: ________________________________</div>
          <div>Aclaración: ___________________________</div>
          <div>Documento: ____________________________</div>
          <div>Fecha y hora: _________________________</div>
        </div>
        <div style={{ marginTop: 18, fontSize: 11, color: "#6b7280" }}>
          Unidad/es: {unidades || "—"} · Citación n.º {a.id ? `${a.id.slice(0, 6)}-${inv.userid}` : inv.userid}
        </div>
      </div>
    </div>
  );
}

/**
 * Citaciones imprimibles: una hoja por propietario con la convocatoria y la
 * constancia de recepción, para entregar en mano a quien no tiene domicilio
 * electrónico (o cuyo envío falló). Después se marca "entregada en mano".
 */
export default function CitacionesImprimibles({ asamblea, invitaciones, edificioNombre }) {
  const { showToast } = useToast();
  const ref = useRef(null);
  const [lista, setLista] = useState([]);
  const [generando, setGenerando] = useState(false);
  const enPapel = (invitaciones || []).filter(requierePapel);
  const todas = invitaciones || [];

  const imprimir = async (cuales) => {
    if (!cuales.length) return;
    setGenerando(true);
    setLista(cuales);
    try {
      // Esperar a que React pinte las hojas antes de capturarlas.
      await new Promise((r) => setTimeout(r, 50));
      const fecha = new Date(Number(asamblea.inicioMs)).toISOString().slice(0, 10);
      await generarPresupuestoPdf(ref.current, `Citaciones-asamblea-${fecha}.pdf`, { download: true });
    } catch (e) {
      console.error("Citaciones imprimibles:", e);
      showToast("No se pudo generar el PDF de citaciones.", "error");
    } finally {
      setGenerando(false);
      setLista([]);
    }
  };

  return (
    <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap", alignItems: "center" }}>
      <button style={btnSecondary} className="habitia-btn-secondary" disabled={generando || !enPapel.length}
        onClick={() => imprimir(enPapel)}>
        <FaPrint /> Citaciones en papel ({enPapel.length})
      </button>
      <button style={btnSecondary} className="habitia-btn-secondary" disabled={generando || !todas.length}
        onClick={() => imprimir(todas)}>
        <FaPrint /> Todas ({todas.length})
      </button>
      {generando && <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Generando PDF…</span>}
      <div ref={ref} aria-hidden="true" style={{ position: "absolute", left: -10000, top: 0 }}>
        {lista.map((inv) => <Hoja key={inv.userid} a={asamblea} inv={inv} edificioNombre={edificioNombre} />)}
      </div>
    </div>
  );
}
